import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { BellOff, BellRing, Inbox, Loader2, LogOut, Mail, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Lockup } from "../components/brand/Logo";
import { api, useAuth } from "./auth";

const STATUSES = ["new", "contacted", "won", "closed"];
const STATUS_STYLE = { new: "bg-acid text-ink", contacted: "bg-lav text-ink", won: "bg-ivory text-ink", closed: "bg-white/10 text-mist" };
const PKG = { essential: "Essential", professional: "Professional", premium: "Premium", maintenance: "Maintenance", not_sure: "Not sure" };
const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const StatusSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-36 border-white/15 bg-char text-ivory" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="border-white/10 bg-char text-ivory">
      {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`${testid}-option-${s}`}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Detail = ({ inq, onStatus }) => (
  <div className="space-y-6 text-sm">
    <div className="flex flex-wrap gap-2">
      <span className="label rounded-full border border-lav px-3 py-1 text-[10px] text-lav" data-testid="detail-language">{inq.language === "es" ? "ES / Spanish" : "EN / English"}</span>
      <span className="label rounded-full border border-white/15 px-3 py-1 text-[10px] text-ivory">{PKG[inq.package]}</span>
    </div>
    <StatusSelect value={inq.status} onChange={(s) => onStatus(inq, s)} testid="detail-status-select" />
    <dl className="grid grid-cols-3 gap-y-3">
      {[["Email", <a key="e" href={`mailto:${inq.email}`} className="text-lav hover:underline">{inq.email}</a>],
        ["Phone", inq.phone ? <a key="p" href={`tel:${inq.phone}`} className="text-lav hover:underline">{inq.phone}</a> : "-"],
        ["Business", inq.business || "-"], ["Budget", inq.budget || "-"], ["Timeline", inq.timeline || "-"],
        ["Website", inq.website || "-"], ["Received", fmt(inq.created_at)], ["Email alert", inq.alert_sent ? "Sent" : "Not sent"]].map(([k, v]) => (
        <div key={k} className="contents"><dt className="label text-[10px] text-mist">{k}</dt><dd className="col-span-2 break-words text-ivory">{v}</dd></div>
      ))}
    </dl>
    <div>
      <p className="label mb-2 text-[10px] text-mist">Project details</p>
      <p className="whitespace-pre-wrap leading-relaxed text-ivory/90" data-testid="detail-project-details">{inq.details}</p>
    </div>
    <div className="flex gap-3">
      <a href={`mailto:${inq.email}`} className="label flex items-center gap-2 rounded-full bg-lav px-5 py-3 text-ink" data-testid="detail-reply-email"><Mail className="h-3.5 w-3.5" />Reply</a>
      {inq.phone && <a href={`tel:${inq.phone}`} className="label flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-ivory" data-testid="detail-call"><Phone className="h-3.5 w-3.5" />Call</a>}
    </div>
  </div>
);

export default function AdminInbox() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => toast.error("Could not load enquiries"));
    api.get("/admin/settings").then((r) => setAlerts(r.data.email_alerts_on)).catch(() => {});
  }, [user]);

  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, (items || []).filter((i) => i.status === s).length])), [items]);
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="h-6 w-6 animate-spin text-lav" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const shown = (items || []).filter((i) => filter === "all" || i.status === filter);
  const open = (items || []).find((i) => i.id === openId);
  const onStatus = async (inq, status) => {
    try {
      const { data } = await api.patch(`/admin/inquiries/${inq.id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  return (
    <div className="min-h-screen bg-ink text-ivory" data-testid="admin-inbox">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-5">
          <div className="w-40"><Lockup className="w-full" /></div>
          <button onClick={logout} className="label flex items-center gap-2 text-mist hover:text-ivory" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="label text-lav">Private inbox</p>
            <h1 className="mt-3 font-display text-5xl font-bold uppercase tracking-tight">Enquiries</h1>
          </div>
          {alerts !== null && (
            <p className={`flex items-center gap-2 text-sm ${alerts ? "text-acid" : "text-mist"}`} data-testid="admin-alert-status">
              {alerts ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              {alerts ? "Email alerts are on" : "Email alerts are off until an alert address is added"}
            </p>
          )}
        </div>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-filters">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
              className={`label rounded-full border px-4 py-2 text-[10px] transition-colors ${filter === s ? "border-lav bg-lav text-ink" : "border-white/15 text-mist hover:text-ivory"}`}>
              {s} {s === "all" ? (items || []).length : counts[s]}
            </button>
          ))}
        </div>
        <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
          {items === null && <div className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-lav" /></div>}
          {items && !shown.length && (
            <div className="flex flex-col items-center gap-3 py-20 text-mist" data-testid="admin-empty"><Inbox className="h-8 w-8" />No enquiries here yet.</div>
          )}
          {shown.map((inq) => (
            <button key={inq.id} onClick={() => setOpenId(inq.id)} data-testid={`inquiry-row-${inq.id}`}
              className="grid w-full grid-cols-12 items-center gap-3 py-5 text-left transition-colors hover:bg-white/[0.03]">
              <span className="col-span-12 md:col-span-3">
                <span className="block font-display text-lg font-semibold">{inq.name}</span>
                <span className="block truncate text-xs text-mist">{inq.business || inq.email}</span>
              </span>
              <span className="col-span-4 text-sm text-ivory/80 md:col-span-2">{PKG[inq.package]}</span>
              <span className="label col-span-2 text-[10px] text-lav md:col-span-1">{inq.language.toUpperCase()}</span>
              <span className="col-span-6 truncate text-sm text-mist md:col-span-3">{inq.details}</span>
              <span className="col-span-6 text-xs text-mist md:col-span-2">{fmt(inq.created_at)}</span>
              <span className="col-span-6 text-right md:col-span-1">
                <span className={`label rounded-full px-3 py-1 text-[9px] ${STATUS_STYLE[inq.status]}`}>{inq.status}</span>
              </span>
            </button>
          ))}
        </div>
      </main>
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full overflow-y-auto border-white/10 bg-ink text-ivory sm:max-w-lg" data-testid="inquiry-detail-sheet">
          {open && (
            <>
              <SheetHeader><SheetTitle className="font-display text-3xl font-bold text-ivory">{open.name}</SheetTitle></SheetHeader>
              <div className="mt-6"><Detail inq={open} onStatus={onStatus} /></div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
